import React, { useMemo, useState } from "react";
import {
    Clock,
    Bell,
    History,
    Umbrella,
    Receipt,
    Plane,
    FileText,
    CheckCircle2,
    XCircle,
    AlertCircle,
    TrendingUp,
    CalendarDays,
} from "lucide-react";
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip as RechartsTooltip,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    AreaChart,
    Area,
} from "recharts";
import "../DashboardStyles/DashboardCollabRH.css";

/* Charte */
const COLORS = {
    primary: "#204385",
    secondary: "#ee645f",
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    info: "#3b82f6",
    grey: "#6b7280",
};

const CHART_COLORS = ["#204385", "#ee645f", "#10b981", "#f59e0b", "#3b82f6", "#a855f7"];

/* Mock data (reprend ton ancien) */
const mockEmployeeData = {
    employee: {
        name: "Youssef Benali",
        matricule: "EMP-2024-0156",
        email: "youssef.benali@entreprise.ma",
        phone: "+212 6 12 34 56 78",
        department: "IT - Développement",
        position: "Développeur Full Stack Senior",
        manager: "Mohammed Alami",
        hireDate: "2021-03-15",
        avatar: "YB",
    },
    leaves: {
        totalAnnual: 22,
        taken: 9,
        remaining: 13,
        pending: 2,
        approved: 7,
        rejected: 0,
        history: [
            { type: "Congé annuel", startDate: "2025-01-05", endDate: "2025-01-10", days: 5, status: "Approuvé" },
            { type: "RTT", startDate: "2025-02-14", endDate: "2025-02-14", days: 1, status: "Approuvé" },
            { type: "Congé annuel", startDate: "2025-03-20", endDate: "2025-03-22", days: 3, status: "Approuvé" },
            { type: "Congé annuel", startDate: "2025-08-01", endDate: "2025-08-05", days: 5, status: "En attente" },
        ],
        monthlyUsage: [
            { month: "Jan", jours: 5 },
            { month: "Fév", jours: 1 },
            { month: "Mar", jours: 3 },
            { month: "Avr", jours: 0 },
            { month: "Mai", jours: 0 },
            { month: "Juin", jours: 0 },
            { month: "Juil", jours: 0 },
            { month: "Aoû", jours: 0 },
            { month: "Sep", jours: 0 },
        ],
        byType: [
            { name: "Congé annuel", value: 8, color: COLORS.info },
            { name: "RTT", value: 1, color: COLORS.success },
            { name: "Maladie", value: 0, color: COLORS.error },
        ],
    },
    expenses: {
        currentMonth: 2850,
        pending: 1,
        approved: 3,
        rejected: 0,
        ytd: 18420,
        history: [
            { date: "2025-09-15", category: "Déplacement", amount: 450, status: "Approuvé" },
            { date: "2025-09-12", category: "Restauration", amount: 280, status: "Approuvé" },
            { date: "2025-09-08", category: "Hébergement", amount: 1200, status: "Approuvé" },
            { date: "2025-09-25", category: "Transport", amount: 920, status: "En attente" },
        ],
        byCategory: [
            { name: "Déplacement", value: 8200 },
            { name: "Restauration", value: 4850 },
            { name: "Hébergement", value: 3900 },
            { name: "Transport", value: 1470 },
        ],
        monthlyTrend: [
            { month: "Jan", montant: 1200 },
            { month: "Fév", montant: 1850 },
            { month: "Mar", montant: 2100 },
            { month: "Avr", montant: 1950 },
            { month: "Mai", montant: 2450 },
            { month: "Juin", montant: 2980 },
            { month: "Juil", montant: 1800 },
            { month: "Aoû", montant: 1240 },
            { month: "Sep", montant: 2850 },
        ],
    },
    missions: {
        ongoing: 1,
        completed: 5,
        upcoming: 1,
        history: [
            { destination: "Casablanca", startDate: "2025-09-20", endDate: "2025-09-22", type: "Formation", status: "En cours" },
            { destination: "Rabat", startDate: "2025-08-05", endDate: "2025-08-05", type: "Client", status: "Terminé" },
            { destination: "Marrakech", startDate: "2025-07-12", endDate: "2025-07-14", type: "Technique", status: "Terminé" },
            { destination: "Paris", startDate: "2025-10-15", endDate: "2025-10-18", type: "Formation", status: "Planifié" },
        ],
        byType: [
            { name: "Formation", value: 3 },
            { name: "Client", value: 2 },
            { name: "Technique", value: 1 },
        ],
    },
    attestations: {
        total: 4,
        thisMonth: 1,
        history: [
            { type: "Attestation de travail", requestDate: "2025-09-10", deliveryDate: "2025-09-11", status: "Délivrée" },
            { type: "Attestation de salaire", requestDate: "2025-06-15", deliveryDate: "2025-06-15", status: "Délivrée" },
            { type: "Certificat de travail", requestDate: "2025-03-20", deliveryDate: "2025-03-21", status: "Délivrée" },
        ],
    },
    activity: {
        totalRequests: 24,
        pendingApproval: 4,
        avgResponseTime: 1.5,
        notifications: 8,
    },
    recentActivities: [
        { type: "expense", title: "Note de frais soumise", description: "Transport - 920 MAD", date: "2025-09-25", status: "pending" },
        { type: "leave", title: "Demande de congé approuvée", description: "Congé annuel - 5 jours (1-5 Août)", date: "2025-07-20", status: "approved" },
        { type: "mission", title: "Mission en cours", description: "Formation à Casablanca", date: "2025-09-20", status: "ongoing" },
        { type: "attestation", title: "Attestation générée", description: "Attestation de travail", date: "2025-09-11", status: "completed" },
    ],
};

const StatusBadge = ({ status }) => {
    const map = {
        "Approuvé": { cls: "dcrhBadge dcrhBadge--ok", label: "Approuvé" },
        "En attente": { cls: "dcrhBadge dcrhBadge--warn", label: "En attente" },
        "Rejeté": { cls: "dcrhBadge dcrhBadge--no", label: "Rejeté" },
        "En cours": { cls: "dcrhBadge dcrhBadge--info", label: "En cours" },
        "Terminé": { cls: "dcrhBadge dcrhBadge--ok", label: "Terminé" },
        "Planifié": { cls: "dcrhBadge dcrhBadge--info", label: "Planifié" },
        "Délivrée": { cls: "dcrhBadge dcrhBadge--ok", label: "Délivrée" },
    };
    const c = map[status] || { cls: "dcrhBadge", label: status };
    return <span className={c.cls}>{c.label}</span>;
};

const StatCard = ({ title, value, icon, subtitle, tone = "primary" }) => (
    <div className="dcrhStatCard">
        <div className="dcrhStatLeft">
            <div className="dcrhStatTitle">{title}</div>
            <div className={`dcrhStatValue dcrhStatValue--${tone}`}>{value}</div>
            {subtitle && <div className="dcrhSub">{subtitle}</div>}
        </div>
        <div className={`dcrhAvatar dcrhAvatar--${tone}`}>{icon}</div>
    </div>
);

const DashboardCollabRH = () => {
    const [data] = useState(mockEmployeeData);
    const [loading, setLoading] = useState(false);
    const [dateDebut, setDateDebut] = useState("");
    const [dateFin, setDateFin] = useState("");

    const handleRefresh = () => {
        setLoading(true);
        setTimeout(() => setLoading(false), 900);
    };

    const leavePercentage = useMemo(() => {
        if (!data.leaves.totalAnnual) return 0;
        return (data.leaves.taken / data.leaves.totalAnnual) * 100;
    }, [data.leaves]);

    const progressStyle = { width: `${Math.min(100, Math.max(0, leavePercentage))}%` };

    const activityIcon = (type) => {
        if (type === "expense") return <Receipt size={16} />;
        if (type === "leave") return <Umbrella size={16} />;
        if (type === "mission") return <Plane size={16} />;
        return <FileText size={16} />;
    };

    const activityTone = (status) => {
        if (status === "approved" || status === "completed") return "ok";
        if (status === "pending") return "warn";
        if (status === "ongoing") return "info";
        return "primary";
    };

    return (
        <div className="dcrhPage">
            <div className="dcrhWrap">
                {/* Header compact */}
                <div className="dcrhHeader">
                    <div>
                        <h1>Tableau de bord</h1>

                    </div>

                    {/*<button className="dcrhBtn dcrhBtn--outline" onClick={handleRefresh} disabled={loading}>*/}
                    {/*    <Clock size={16} />*/}
                    {/*    {loading ? "Actualisation..." : "Actualiser"}*/}
                    {/*</button>*/}
                </div>

                {/* Filtres dates */}
                <div className="dcrhCard">
                    <div className="dcrhControls">
                        <div className="dcrhField">
                            <label>DE :</label>
                            <input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} />
                        </div>
                        <div className="dcrhField">
                            <label>À :</label>
                            <input type="date" value={dateFin} min={dateDebut || undefined} onChange={(e) => setDateFin(e.target.value)} />
                        </div>
                        {/*<div className="dcrhHint">*/}
                        {/*    <CalendarDays size={16} />*/}
                        {/*    Filtre (UI) — branchement API ensuite*/}
                        {/*</div>*/}
                    </div>
                </div>

                {/* KPIs activité */}
                <div className="dcrhGrid4">
                    <StatCard title="Demandes en attente" value={data.activity.pendingApproval} icon={<AlertCircle size={22} />} subtitle="Nécessitent une action" tone="warn" />
                    <StatCard title="Notifications" value={data.activity.notifications} icon={<Bell size={22} />} subtitle="Nouvelles mises à jour" tone="info" />
                    <StatCard title="Demandes totales" value={data.activity.totalRequests} icon={<History size={22} />} subtitle="Cette année" tone="primary" />
                    <StatCard title="Temps de réponse moyen" value={`${data.activity.avgResponseTime}j`} icon={<Clock size={22} />} subtitle="Pour vos demandes" tone="ok" />
                </div>

                {/* ===== Congés ===== */}
                <div className="dcrhSectionTitle">
                    <Umbrella size={18} /> Mes Congés
                </div>

                <div className="dcrhGrid2">
                    {/* Solde + Progress */}
                    <div className="dcrhCard">
                        <div className="dcrhCardHead">
                            <div className="dcrhCardTitle">Solde de congés annuels</div>
                            <span className="dcrhPill dcrhPill--ok">{data.leaves.remaining} jours disponibles</span>
                        </div>

                        <div className="dcrhMiniKpis">
                            <div className="dcrhMiniKpi dcrhMiniKpi--pri">
                                <div className="dcrhMiniVal">{data.leaves.totalAnnual}</div>
                                <div className="dcrhMiniLab">Total annuel</div>
                            </div>
                            <div className="dcrhMiniKpi dcrhMiniKpi--sec">
                                <div className="dcrhMiniVal">{data.leaves.taken}</div>
                                <div className="dcrhMiniLab">Jours pris</div>
                            </div>
                            <div className="dcrhMiniKpi dcrhMiniKpi--ok">
                                <div className="dcrhMiniVal">{data.leaves.remaining}</div>
                                <div className="dcrhMiniLab">Jours restants</div>
                            </div>
                        </div>

                        <div className="dcrhProgressBlock">
                            <div className="dcrhProgressTop">
                                <span>Utilisation des congés</span>
                                <span className="dcrhPrimary">{leavePercentage.toFixed(0)}%</span>
                            </div>
                            <div className="dcrhProgress">
                                <div className="dcrhProgressFill" style={progressStyle} />
                            </div>
                        </div>

                        <div className="dcrhDivider" />

                        <div className="dcrhStats3">
                            <div className="dcrhStatLine">
                                <CheckCircle2 size={18} className="ok" />
                                <div>
                                    <div className="dcrhStatLineVal">{data.leaves.approved}</div>
                                    <div className="dcrhSub">Approuvées</div>
                                </div>
                            </div>
                            <div className="dcrhStatLine">
                                <AlertCircle size={18} className="warn" />
                                <div>
                                    <div className="dcrhStatLineVal">{data.leaves.pending}</div>
                                    <div className="dcrhSub">En attente</div>
                                </div>
                            </div>
                            <div className="dcrhStatLine">
                                <XCircle size={18} className="no" />
                                <div>
                                    <div className="dcrhStatLineVal">{data.leaves.rejected}</div>
                                    <div className="dcrhSub">Rejetées</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Pie type */}
                    <div className="dcrhCard">
                        <div className="dcrhCardTitle">Répartition par type</div>
                        <div className="dcrhChartBox">
                            <ResponsiveContainer width="100%" height={260}>
                                <PieChart>
                                    <Pie data={data.leaves.byType} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                                        {data.leaves.byType.map((e, idx) => (
                                            <Cell key={idx} fill={e.color || CHART_COLORS[idx % CHART_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip />
                                </PieChart>
                            </ResponsiveContainer>

                            <div className="dcrhLegend">
                                {data.leaves.byType.map((e, idx) => (
                                    <div key={e.name} className="dcrhLegendItem">
                                        <span className="dcrhLegendDot" style={{ background: e.color || CHART_COLORS[idx % CHART_COLORS.length] }} />
                                        <span className="dcrhLegendName">{e.name}</span>
                                        <span className="dcrhLegendVal">{e.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bar monthly + history */}
                <div className="dcrhGrid2">
                    <div className="dcrhCard">
                        <div className="dcrhCardTitle">Utilisation mensuelle</div>
                        <div className="dcrhChartBox">
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={data.leaves.monthlyUsage}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <RechartsTooltip />
                                    <Bar dataKey="jours" fill={COLORS.info} radius={[8, 8, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="dcrhCard dcrhCard--noPad">
                        <div className="dcrhCardHead dcrhCardHead--pad">
                            <div className="dcrhCardTitle">Historique récent</div>
                        </div>

                        <div className="dcrhList">
                            {data.leaves.history.slice(0, 4).map((leave, idx) => (
                                <div key={idx} className="dcrhListRow">
                                    <div className="dcrhListIcon dcrhListIcon--info">
                                        <CalendarDays size={16} />
                                    </div>
                                    <div className="dcrhListBody">
                                        <div className="dcrhListTop">
                                            <span className="dcrhPrimary">{leave.type}</span>
                                            <StatusBadge status={leave.status} />
                                        </div>
                                        <div className="dcrhSub">
                                            {leave.startDate} → {leave.endDate} ({leave.days} jour{leave.days > 1 ? "s" : ""})
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ===== Notes de frais ===== */}
                <div className="dcrhSectionTitle">
                    <Receipt size={18} /> Mes Notes de Frais
                </div>

                <div className="dcrhGrid4">
                    <StatCard title="Mois en cours" value={`${data.expenses.currentMonth} MAD`} icon={<Receipt size={22} />} tone="secondary" />
                    <StatCard title="En attente" value={data.expenses.pending} icon={<AlertCircle size={22} />} tone="warn" />
                    <StatCard title="Approuvées" value={data.expenses.approved} icon={<CheckCircle2 size={22} />} tone="ok" />
                    <StatCard title="Total annuel" value={`${(data.expenses.ytd / 1000).toFixed(1)}K MAD`} icon={<TrendingUp size={22} />} tone="primary" />
                </div>

                <div className="dcrhGrid2">
                    <div className="dcrhCard">
                        <div className="dcrhCardTitle">Évolution mensuelle</div>
                        <div className="dcrhChartBox">
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={data.expenses.monthlyTrend}>
                                    <defs>
                                        <linearGradient id="dcrhExp" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={COLORS.secondary} stopOpacity={0.8} />
                                            <stop offset="95%" stopColor={COLORS.secondary} stopOpacity={0.12} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <RechartsTooltip />
                                    <Area type="monotone" dataKey="montant" stroke={COLORS.secondary} fill="url(#dcrhExp)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="dcrhCard">
                        <div className="dcrhCardTitle">Par catégorie (YTD)</div>
                        <div className="dcrhChartBox">
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={data.expenses.byCategory} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" />
                                    <YAxis dataKey="name" type="category" width={110} />
                                    <RechartsTooltip />
                                    <Bar dataKey="value" fill={COLORS.secondary} radius={[0, 8, 8, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                <div className="dcrhCard dcrhCard--noPad">
                    <div className="dcrhCardHead dcrhCardHead--pad">
                        <div className="dcrhCardTitle">Dernières notes de frais</div>
                    </div>
                    <div className="dcrhList">
                        {data.expenses.history.map((expense, idx) => (
                            <div key={idx} className="dcrhListRow">
                                <div className="dcrhListIcon dcrhListIcon--secondary">
                                    <Receipt size={16} />
                                </div>
                                <div className="dcrhListBody">
                                    <div className="dcrhListTop">
                                        <span className="dcrhPrimary">{expense.category}</span>
                                        <div className="dcrhRightPack">
                                            <span className="dcrhAmount">{expense.amount} MAD</span>
                                            <StatusBadge status={expense.status} />
                                        </div>
                                    </div>
                                    <div className="dcrhSub">{expense.date}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ===== Missions ===== */}
                <div className="dcrhSectionTitle">
                    <Plane size={18} /> Mes Missions
                </div>

                <div className="dcrhGrid3">
                    <StatCard title="En cours" value={data.missions.ongoing} icon={<Plane size={22} />} tone="info" />
                    <StatCard title="Terminées" value={data.missions.completed} icon={<CheckCircle2 size={22} />} tone="ok" />
                    <StatCard title="À venir" value={data.missions.upcoming} icon={<CalendarDays size={22} />} tone="warn" />
                </div>

                <div className="dcrhGrid2">
                    <div className="dcrhCard">
                        <div className="dcrhCardTitle">Missions par type</div>
                        <div className="dcrhChartBox">
                            <ResponsiveContainer width="100%" height={260}>
                                <PieChart>
                                    <Pie data={data.missions.byType} dataKey="value" nameKey="name" outerRadius={90} paddingAngle={3}>
                                        {data.missions.byType.map((e, idx) => (
                                            <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="dcrhCard dcrhCard--noPad">
                        <div className="dcrhCardHead dcrhCardHead--pad">
                            <div className="dcrhCardTitle">Historique des missions</div>
                        </div>
                        <div className="dcrhList">
                            {data.missions.history.map((m, idx) => (
                                <div key={idx} className="dcrhListRow">
                                    <div className="dcrhListIcon dcrhListIcon--info">
                                        <Plane size={16} />
                                    </div>
                                    <div className="dcrhListBody">
                                        <div className="dcrhListTop">
                                            <span className="dcrhPrimary">{m.destination}</span>
                                            <StatusBadge status={m.status} />
                                        </div>
                                        <div className="dcrhSub">
                                            {m.type} • {m.startDate} → {m.endDate}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ===== Attestations ===== */}
                <div className="dcrhSectionTitle">
                    <FileText size={18} /> Mes Attestations
                </div>

                <div className="dcrhGrid3">
                    <StatCard title="Total demandées" value={data.attestations.total} icon={<FileText size={22} />} tone="primary" />
                    <StatCard title="Ce mois" value={data.attestations.thisMonth} icon={<CalendarDays size={22} />} tone="info" />
                    <div className="dcrhCard">
                        <div className="dcrhCardTitle">Dernières attestations</div>
                        <div className="dcrhList dcrhList--dense">
                            {data.attestations.history.slice(0, 2).map((a, idx) => (
                                <div key={idx} className="dcrhListRow dcrhListRow--dense">
                                    <div className="dcrhListBody">
                                        <div className="dcrhListTop">
                                            <span className="dcrhPrimary">{a.type}</span>
                                            <StatusBadge status={a.status} />
                                        </div>
                                        <div className="dcrhSub">Demandée le {a.requestDate}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ===== Activités ===== */}
                <div className="dcrhSectionTitle">
                    <History size={18} /> Activités Récentes
                </div>

                <div className="dcrhCard">
                    <div className="dcrhActivities">
                        {data.recentActivities.map((a, idx) => (
                            <div key={idx} className="dcrhActivityItem">
                                <div className={`dcrhActivityAvatar dcrhActivityAvatar--${activityTone(a.status)}`}>
                                    {activityIcon(a.type)}
                                </div>
                                <div className="dcrhActivityBody">
                                    <div className="dcrhPrimary">{a.title}</div>
                                    <div className="dcrhSub">{a.description}</div>
                                </div>
                                <div className="dcrhSub">{a.date}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="dcrhFooter">
                    Dernière mise à jour : {new Date().toLocaleString("fr-FR")}
                </div>
            </div>
        </div>
    );
};

export default DashboardCollabRH;
